import React, { useMemo } from 'react';
import { Pagination, Form, Spinner } from 'react-bootstrap';

const TableFooter = ({
                         totalItems = 0,
                         page = 1,
                         pageSize = 10,
                         onPageChange = () => {},
                         onPageSizeChange = () => {},
                         pageSizeOptions = [10, 25, 50, 100],
                         loading = false,
                         className = '',
                     }) => {
    const pageCount = useMemo(() => {
        return Math.max(1, Math.ceil(Number(totalItems) / Math.max(1, Number(pageSize))));
    }, [totalItems, pageSize]);

    const currentPage = Math.min(Math.max(1, Number(page)), pageCount);

    const firstItemIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const lastItemIndex = Math.min(currentPage * pageSize, totalItems);

    const canGoPrevious = currentPage > 1 && !loading;
    const canGoNext = currentPage < pageCount && !loading;

    const renderPageNumbers = () => {
        const maxToShow = 5;
        const numbers = [];

        const half = Math.floor((maxToShow - 1) / 2);
        let start = Math.max(1, currentPage - half);
        let end = Math.min(pageCount, start + maxToShow - 1);

        start = Math.max(1, Math.min(start, Math.max(1, pageCount - maxToShow + 1)));
        end = Math.min(pageCount, Math.max(end, Math.min(pageCount, maxToShow)));

        if (start > 1) {
            numbers.push(1);

            if (start > 2) {
                numbers.push('ellipsis-start');
            }
        }

        for (let number = start; number <= end; number++) {
            numbers.push(number);
        }

        if (end < pageCount) {
            if (end < pageCount - 1) {
                numbers.push('ellipsis-end');
            }

            numbers.push(pageCount);
        }

        return numbers.map((number, index) => {
            if (typeof number !== 'number') {
                return <Pagination.Ellipsis key={`${number}-${index}`} disabled />;
            }

            return (
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    disabled={loading}
                    onClick={() => {
                        if (number !== currentPage) {
                            onPageChange(number);
                        }
                    }}
                >
                    {number}
                </Pagination.Item>
            );
        });
    };

    return (
        <div
            className={`d-flex flex-wrap gap-3 justify-content-between align-items-center py-2 ${className}`}
        >
            <div className="d-flex align-items-center gap-2">
                <span className="text-muted">
                    Rijen per pagina:
                </span>

                <Form.Select
                    size="sm"
                    value={pageSize}
                    disabled={loading}
                    onChange={(event) => onPageSizeChange(Number(event.target.value))}
                    style={{ width: 90 }}
                    aria-label="Rijen per pagina"
                >
                    {pageSizeOptions.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </Form.Select>

                {loading && (
                    <span className="text-muted small d-inline-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        Laden...
                    </span>
                )}
            </div>

            <div className="text-muted small">
                {totalItems === 0
                    ? '0 van 0'
                    : `Items ${firstItemIndex}–${lastItemIndex} van ${totalItems}`}
            </div>

            <Pagination className="mb-0">
                <Pagination.First
                    onClick={() => onPageChange(1)}
                    disabled={!canGoPrevious}
                />

                <Pagination.Prev
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={!canGoPrevious}
                />

                {renderPageNumbers()}

                <Pagination.Next
                    onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
                    disabled={!canGoNext}
                />

                <Pagination.Last
                    onClick={() => onPageChange(pageCount)}
                    disabled={!canGoNext}
                />
            </Pagination>
        </div>
    );
};

export default TableFooter;
